import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useRevit } from '../../contexts/RevitContext';
import { handleLoginWithYouTube } from '../../utils/apiCalls';
import { useAuth } from '@clerk/clerk-react';
import './Content.css';
import { NavLink } from 'react-router-dom';
import { TrendingUp, TrendingDown } from 'lucide-react';
import Icon from '../Icon';
import { logoutFromYt } from '../../utils/apiCalls';
import { applyDashboardTimeframe } from '../../utils/dateRange';

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const DAY_MS = 24 * 60 * 60 * 1000;

function addDays(date, days) {
    return new Date(date.getTime() + days * DAY_MS);
}

function inWindow(timestamp, window) {
    const time = new Date(timestamp).getTime();
    const start = window.start.getTime();
    const end = window.end.getTime();
    if (time < start) return false;
    return window.inclusiveEnd ? time <= end : time < end;
}

function sumInWindow(rows, window) {
    let sum = 0;
    for (const row of rows) {
        if (!inWindow(row.timestamp, window)) continue;
        sum += Number(row.value) || 0;
    }
    return sum;
}

function formatMoney(value, currency) {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(value || 0);
}

function countInWindow(rows, window, predicate) {
    let count = 0;
    for (const row of rows) {
        if (!inWindow(row.timestamp, window)) continue;
        if (predicate && !predicate(row)) continue;
        count += 1;
    }
    return count;
}

function formatCount(value) {
    const rounded = Math.round(value * 10) / 10;
    return rounded.toLocaleString(undefined, {
        maximumFractionDigits: Number.isInteger(rounded) ? 0 : 1
    });
}

function formatRate(rate) {
    if (rate == null || !Number.isFinite(rate)) return '—';
    return `${(rate * 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })}%`;
}

function percentChange(current, previous) {
    if (current == null || previous == null || !Number.isFinite(current) || !Number.isFinite(previous) || previous === 0) {
        return null;
    }
    return ((current - previous) / Math.abs(previous)) * 100;
}

const SUMMARY_TEXT = {
    traffic: (offer) => offer
        ? `This month traffic was lower to ${offer}. Try posting more and check your CTAs.`
        : `This month traffic was lower overall. Try posting more and check your CTAs.`,
    attributedClicks: (offer) => offer
        ? `Attributed clicks were lower for ${offer}. Ensure you have place tracking links in every source where traffic is coming from.`
        : `Attributed clicks were lower overall. Ensure you have place tracking links in every source where traffic is coming from.`,
    emails: (offer) => offer
        ? `Email signups were lower for ${offer}. Try a clearer lead magnet and mention it earlier in your videos.`
        : `Email signups were lower overall. Try a clearer lead magnet and mention it earlier in your videos.`,
    calls: (offer) => offer
        ? `Call bookings were lower for ${offer}. Check the booking page and mention the call earlier.`
        : `Call bookings were lower overall. Check the booking page and mention the call earlier.`,
    sales: (offer) => offer
        ? `Sales were lower for ${offer}. Look at the videos that used to convert and make more like those.`
        : `Sales were lower overall. Look at the videos that used to convert and make more like those.`,
    clickToSale: (offer) => offer
        ? `Click to sale was lower for ${offer}. Check your CTAs & video topics to ensure you target the correct avatar.`
        : `Click to sale was lower overall. Check your CTAs & video topics to ensure you target the correct avatar.`
};

function formatChange(current, previous) {
    if (current == null || previous == null || !Number.isFinite(current) || !Number.isFinite(previous)) {
        return { text: '—', direction: 'flat' };
    }
    if (previous === 0) {
        if (current === 0) return { text: '0%', direction: 'flat' };
        return { text: '—', direction: 'flat' };
    }
    const pct = ((current - previous) / Math.abs(previous)) * 100;
    const abs = Math.abs(pct);
    const rounded = abs >= 100 ? Math.round(pct) : Math.round(pct * 10) / 10;
    const shown = Number.isInteger(rounded) ? String(Math.trunc(rounded)) : rounded.toFixed(1);
    const sign = rounded > 0 ? '+' : '';
    return {
        text: `${sign}${shown}%`,
        direction: rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'flat'
    };
}

function buildFunnel(events, selectedOffer, archivedOfferNames, currency) {
    const now = new Date();
    const currentWindow = { start: addDays(now, -30), end: now, inclusiveEnd: true };
    const previousWindow = { start: addDays(currentWindow.start, -30), end: currentWindow.start };
    const yearStart = new Date(now);
    yearStart.setFullYear(yearStart.getFullYear() - 1);
    const yearWindow = { start: yearStart, end: now, inclusiveEnd: true };
    const yearDays = (yearWindow.end.getTime() - yearWindow.start.getTime()) / DAY_MS;

    const archivedNames = new Set(archivedOfferNames || []);
    const offerRows = (rows) => rows.filter((row) => {
        if (archivedNames.has(row.offer_name)) return false;
        return selectedOffer === 'all' || row.offer_name === selectedOffer;
    });

    const clicks = offerRows(events.clicks || []);
    const sales = offerRows(events.sales || []);
    const signups = offerRows(events.signups || []);
    const calls = offerRows(events.call_bookings || []);
    const hasClickResource = (click) => !!(click?.type || click?.resource_name || click?.youtube_title);

    const metric = (rows, predicate) => {
        const thisMonth = countInWindow(rows, currentWindow, predicate);
        const lastMonth = countInWindow(rows, previousWindow, predicate);
        const yearCount = countInWindow(rows, yearWindow, predicate);
        const normal = yearDays > 0 ? (yearCount * 30) / yearDays : 0;
        return { thisMonth, lastMonth, normal };
    };

    const traffic = metric(clicks);
    const attributedClicks = metric(clicks, hasClickResource);
    const emails = metric(signups);
    const bookings = metric(calls);
    const paidSales = metric(sales);
    const revenue = {
        thisMonth: sumInWindow(sales, currentWindow),
        lastMonth: sumInWindow(sales, previousWindow),
        normal: yearDays > 0 ? (sumInWindow(sales, yearWindow) * 30) / yearDays : 0
    };

    const rate = (salesMetric, trafficMetric, key) => {
        const clicksValue = trafficMetric[key];
        if (!clicksValue) return null;
        return salesMetric[key] / clicksValue;
    };

    const clickToSale = {
        thisMonth: rate(paidSales, traffic, 'thisMonth'),
        lastMonth: rate(paidSales, traffic, 'lastMonth'),
        normal: rate(paidSales, traffic, 'normal')
    };

    const countRow = (key, label, series, formatValue = formatCount, help) => {
        const change = formatChange(series.thisMonth, series.lastMonth);
        return {
            key,
            label,
            help,
            thisMonth: formatValue(series.thisMonth),
            lastMonth: formatValue(series.lastMonth),
            normal: formatValue(series.normal),
            change: change.text,
            direction: change.direction
        };
    };

    const rateChange = formatChange(clickToSale.thisMonth, clickToSale.lastMonth);

    const namedOffer = selectedOffer === 'all' ? null : selectedOffer;
    const summaries = [
        { key: 'traffic', pct: percentChange(traffic.thisMonth, traffic.lastMonth) },
        { key: 'attributedClicks', pct: percentChange(attributedClicks.thisMonth, attributedClicks.lastMonth) },
        { key: 'emails', pct: percentChange(emails.thisMonth, emails.lastMonth) },
        { key: 'calls', pct: percentChange(bookings.thisMonth, bookings.lastMonth) },
        { key: 'sales', pct: percentChange(paidSales.thisMonth, paidSales.lastMonth) },
        { key: 'clickToSale', pct: percentChange(clickToSale.thisMonth, clickToSale.lastMonth) }
    ]
        .filter((item) => item.pct != null && item.pct < 0)
        .sort((a, b) => a.pct - b.pct)
        .slice(0, 2)
        .map((item) => ({ key: item.key, direction: 'down', text: SUMMARY_TEXT[item.key](namedOffer) }));

    if (paidSales.thisMonth > paidSales.lastMonth || revenue.thisMonth > revenue.lastMonth) {
        summaries.unshift({
            key: 'salesUp',
            direction: 'up',
            text: 'Sales are up. To keep the momentum going, take a look at the content that converted the best, and make more of that.'
        });
    }

    return {
        summaries,
        rows: [
            countRow('traffic', 'Traffic', traffic, formatCount, 'This measures all visits to your landing page, not just tracked clicks'),
            countRow('attributedClicks', 'Attributed clicks', attributedClicks, formatCount, 'All traffic that originated from a Revtrack tracking link.'),
            countRow('emails', 'Email signups', emails),
            countRow('calls', 'Call bookings', bookings),
            countRow('sales', 'Sales', paidSales),
            countRow('revenue', 'Revenue', revenue, (value) => formatMoney(value, currency)),
            {
                key: 'clickToSale',
                label: 'Click → sale',
                thisMonth: formatRate(clickToSale.thisMonth),
                lastMonth: formatRate(clickToSale.lastMonth),
                normal: formatRate(clickToSale.normal),
                change: rateChange.text,
                direction: rateChange.direction
            }
        ]
    };
}

const Content = () => {
    const { username, youtubeName, youtubeThumbUrl, setYoutubeName, youtubeLoading } = useRevit();
    const { getToken } = useAuth();
    const [latestVideos, setLatestVideos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [periodEvents, setPeriodEvents] = useState(null);
    const [topResources, setTopResources] = useState(null);
    const [topResourcesTab, setTopResourcesTab] = useState('sales');
    const [contentSort, setContentSort] = useState({ key: null, dir: 'desc' });
    const [currency, setCurrency] = useState('USD');
    const [rankingMetric, setRankingMetric] = useState('clicks');
    // Add state for offer filter
    const [selectedOffer, setSelectedOffer] = useState('all');
    const [offers, setOffers] = useState([]);
    const [archivedOfferNames, setArchivedOfferNames] = useState([]);
    // Add state to track if there are any signup offers
    const [hasSignupOffers, setHasSignupOffers] = useState(false);
    // Add state for latest video metrics
    const [latestVideoMetrics, setLatestVideoMetrics] = useState(null);

    const handleLogout = async () => {
        const token = await getToken();
        try {
            const result = await logoutFromYt(username, token);
            if (result) {
                setYoutubeName(null);
            }
        } catch (error) {
            console.error('Error during logout:', error);
        }
    };

    useEffect(() => {
        const fetchAllData = async () => {
            setIsLoading(true);

            if (!username || youtubeLoading) return;
            
            const token = await getToken();
            
            try {
                const now = new Date();
                const rangeStart = new Date(now);
                rangeStart.setFullYear(rangeStart.getFullYear() - 1);
                const { startDate: contentStart, endDate: contentEnd } = applyDashboardTimeframe('30days');

                // Funnel uses 12 months of events. Top content uses the same 30-day window as Home.
                const promises = [
                    axios.get(`https://${SERVER_URL}/api/offers/${username}`, {
                        params: { includeArchived: 'true' },
                        headers: { 'Authorization': `Bearer ${token}` }
                    }),
                    axios.get(`https://${SERVER_URL}/api/get-data/${username}`, {
                        params: {
                            startDateIncludingPreviousPeriod: rangeStart.toISOString(),
                            endDate: contentEnd.toISOString(),
                            realStartDate: contentStart.toISOString()
                        },
                        headers: { 'Authorization': `Bearer ${token}` }
                    })
                ];
                
                if (youtubeName) {
                    promises.push(
                        axios.get(`https://${SERVER_URL}/api/get-latest-videos/${username}`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        })
                    );
                }
                
                const responses = await Promise.all(promises);
                
                // Process offers
                const fetchedOffers = responses[0].data.offers || [];
                const activeOffers = fetchedOffers.filter((offer) => !offer.is_archived);
                setOffers(activeOffers);
                setArchivedOfferNames(
                    fetchedOffers.filter((offer) => offer.is_archived).map((offer) => offer.name)
                );
                setSelectedOffer((current) => (
                    current === 'all' || activeOffers.some((offer) => offer.name === current) ? current : 'all'
                ));
                setHasSignupOffers(activeOffers.some(offer => offer.offer_type === 'signup'));
                
                const fullData = responses[1].data;

                setPeriodEvents({
                    clicks: fullData.clicks || [],
                    sales: fullData.sales || [],
                    signups: fullData.signups || [],
                    call_bookings: fullData.call_bookings || []
                });
                setTopResources(fullData.topResources || null);
                if (fullData.currency) setCurrency(fullData.currency);
                
                if (youtubeName && responses[2]) {
                    setLatestVideos(responses[2].data.videos);
                }
                
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAllData();
    }, [username, youtubeName, getToken, youtubeLoading]);

    // Wrap getLatestVideoMetrics in useCallback to prevent infinite loops
    const getLatestVideoMetrics = useCallback(() => {
        if (!latestVideos || latestVideos.length === 0) return null;
        console.log(latestVideos)
        const latestVideo = latestVideos[0];
        const now = new Date();
        const latestVideoPublishDate = new Date(latestVideo.publishedAt);
        const timeSincePublish = now - latestVideoPublishDate;

        const getClicksInTimeframe = (video) => {
            const publishDate = new Date(video.publishedAt);
            return video.engagement.clicks.filter(click => {
                const clickDate = new Date(click.timestamp);
                const timeSinceVideoPublish = clickDate - publishDate;
                return timeSinceVideoPublish <= timeSincePublish && 
                       timeSinceVideoPublish >= 0 && 
                       (selectedOffer === 'all' || click.offer_name === selectedOffer);
            }).length;
        };

        const getSalesInTimeframe = (video) => {
            const publishDate = new Date(video.publishedAt);
            return video.engagement.sales.filter(sale => {
                const saleDate = new Date(sale.timestamp);
                const timeSinceVideoPublish = saleDate - publishDate;
                
                return timeSinceVideoPublish <= timeSincePublish && 
                       timeSinceVideoPublish >= 0 && 
                       (selectedOffer === 'all' || sale.offer_name === selectedOffer);
            }).length;
        };
        
        const getCallBookingsInTimeframe = (video) => {
            const publishDate = new Date(video.publishedAt);
            return video.engagement.call_bookings?.filter(booking => {
                const bookingDate = new Date(booking.timestamp);
                const timeSinceVideoPublish = bookingDate - publishDate;
                
                return timeSinceVideoPublish <= timeSincePublish && 
                       timeSinceVideoPublish >= 0 && 
                       (selectedOffer === 'all' || booking.offer_name === selectedOffer);
            }).length || 0;
        };
        
        const getSignupsInTimeframe = (video) => {
            const publishDate = new Date(video.publishedAt);
            return video.engagement.email_signups?.filter(signup => {
                const signupDate = new Date(signup.timestamp);
                const timeSinceVideoPublish = signupDate - publishDate;
                
                return timeSinceVideoPublish <= timeSincePublish && 
                       timeSinceVideoPublish >= 0 && 
                       (selectedOffer === 'all' || signup.offer_name === selectedOffer);
            }).length || 0;
        };

        // Get all videos with their timeframed metrics
        const videosWithMetrics = latestVideos.map(video => ({
            id: video.id,
            title: video.title,
            clicks: getClicksInTimeframe(video),
            sales: getSalesInTimeframe(video),
            signups: getSignupsInTimeframe(video),
            callBookings: getCallBookingsInTimeframe(video)
        }));

        // Sort by selected metric
        const sortedVideos = [...videosWithMetrics].sort((a, b) => {
            if (rankingMetric === 'clicks') {
                return b.clicks - a.clicks;
            } else if (rankingMetric === 'signups') {
                return b.signups - a.signups;
            } else if (rankingMetric === 'callBookings') {
                return b.callBookings - a.callBookings;
            } else {
                return b.sales - a.sales;
            }
        });

        const rank = sortedVideos.findIndex(video => video.id === latestVideo.id) + 1;

        // Get top 10 videos for the tooltip
        const topVideos = sortedVideos.slice(0, 10).map(video => ({
            title: video.title,
            clicks: video.clicks,
            sales: video.sales,
            signups: video.signups,
            callBookings: video.callBookings,
            thumbnail: latestVideos.find(v => v.id === video.id)?.thumbnails?.default?.url
        }));

        const formatTimePeriod = (ms) => {
            const days = Math.floor(ms / (1000 * 60 * 60 * 24));
            const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            if (days === 0) return `First ${hours} hours`;
            return `First ${days} days ${hours} hours`;
        };

        return {
            thumbnail: latestVideo.thumbnails.medium.url,
            title: latestVideo.title,
            rank: `${rank}/${latestVideos.length}`,
            clicks: getClicksInTimeframe(latestVideo),
            sales: getSalesInTimeframe(latestVideo),
            signups: getSignupsInTimeframe(latestVideo),
            callBookings: getCallBookingsInTimeframe(latestVideo),
            views: parseInt(latestVideo.statistics.viewCount),
            ctr: (getClicksInTimeframe(latestVideo) / parseInt(latestVideo.statistics.viewCount) * 100).toFixed(2),
            timeframe: formatTimePeriod(timeSincePublish),
            topVideos: topVideos,
            rankingMetric: rankingMetric
        };
    }, [latestVideos, rankingMetric, selectedOffer]);

    const funnel = useMemo(
        () => (periodEvents ? buildFunnel(periodEvents, selectedOffer, archivedOfferNames, currency) : null),
        [periodEvents, selectedOffer, archivedOfferNames, currency]
    );

    // Add useEffect to calculate metrics when dependencies are ready
    useEffect(() => {
        if (latestVideos && latestVideos.length > 0) {
            const metrics = getLatestVideoMetrics();
            setLatestVideoMetrics(metrics);
        } else {
            setLatestVideoMetrics(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [latestVideos, offers, rankingMetric, selectedOffer]);

    useEffect(() => { setContentSort({ key: null, dir: 'desc' }); }, [topResourcesTab]);

    useEffect(() => {
        if (!topResources) return;
        const next =
            topResources.topRevenue?.length > 0 ? 'sales' :
            topResources.topCalls?.length > 0 ? 'calls' :
            topResources.topEmails?.length > 0 ? 'emails' :
            topResources.topClicks?.length > 0 ? 'clicks' :
            'sales';
        setTopResourcesTab(next);
    }, [topResources]);

    const handleContentSort = (key) =>
        setContentSort(prev => prev.key === key
            ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
            : { key, dir: 'desc' });

    const SortTh = ({ label, sortKey, sort, onSort, className }) => {
        const isActive = sort.key === sortKey;
        return (
            <span
                className={`${className || ''} th-sortable${isActive ? ' th-sort-active' : ''}`}
                onClick={() => onSort(sortKey)}
            >
                {label}
                <span className="th-sort-icon">{isActive ? (sort.dir === 'desc' ? '↓' : '↑') : '↕'}</span>
            </span>
        );
    };

    const formatNumber = (num) => {
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
        return num.toString();
    };

    const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(value);

    return (
        <div className="content-container">
                {(isLoading || youtubeLoading) && (
                    <div className="loading-overlay">
                        <div className="loading-text">Loading...</div>
                    </div>
                )}
                {!isLoading && !youtubeLoading && (
                    <div className="content-wrapper">
                    <div className="youtube-account-container">
                        {youtubeName ? (
                            <div className="youtube-account-info">
                                <p>
                                    Logged in as:
                                </p>
                                <div className="youtube-account-details">
                                    {youtubeThumbUrl && (
                                        <img 
                                            src={youtubeThumbUrl} 
                                            alt="Channel" 
                                            className="channel-thumbnail" 
                                            style={{ width: '28px', height: '28px', borderRadius: '50%', marginRight: '8px', verticalAlign: 'middle' }}
                                        />
                                    )}
                                    {youtubeName}
                                    <Icon 
                                        name="logout" 
                                        className="yt-logout-icon" 
                                        onClick={handleLogout}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div>
                                <button className="youtube-login-button" onClick={() => handleLoginWithYouTube(username)}>
                                    <Icon name="youtube" size={20} />
                                    Login with YouTube
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="stats-container">
                        {/* Left column - Latest video card */}
                        {youtubeName && latestVideoMetrics ? (
                            <div className="latest-video-card">
                                <div>
                                <img 
                                    src={latestVideoMetrics.thumbnail} 
                                    alt="Video thumbnail" 
                                    className="video-thumbnail"
                                />
                                <div className="video-details">
                                    <h3 className="video-title heading-h4">{latestVideoMetrics.title}</h3>
                                    <div className="ranking-controls">
                                        <div className="content-filter-group">
                                            <label htmlFor="ranking-metric" className="body-small-semibold">Rank by:</label>
                                            <select 
                                                id="ranking-metric" 
                                                value={rankingMetric} 
                                                onChange={(e) => setRankingMetric(e.target.value)}
                                            >
                                                <option value="clicks">Clicks</option>
                                                {hasSignupOffers && <option value="signups">Emails</option>}
                                                <option value="callBookings">Call Bookings</option>
                                                <option value="conversions">Sales</option>
                                            </select>
                                        </div>
                                        
                                        <div className="content-filter-group">
                                            <label htmlFor="offer-filter" className="body-small-semibold">Offer:</label>
                                            <select
                                                id="offer-filter"
                                                value={selectedOffer}
                                                onChange={(e) => setSelectedOffer(e.target.value)}
                                            >
                                                <option value="all">All Offers</option>
                                                {offers && (
                                                    offers.map((offer) => (
                                                        <option key={offer.id} value={offer.name}>
                                                            {offer.name}
                                                        </option>
                                                    ))
                                                )
                                                 }
                                            </select>
                                        </div>
                                    </div>
                                    <p className="timeframe body-tiny-regular">{latestVideoMetrics.timeframe} compared to your typical performance:</p>
                                    <div className="metrics-grid">
                                        <div className="metric">
                                            <label className="body-small-regular">Ranking by {
                                                rankingMetric === 'clicks' 
                                                    ? 'Clicks' 
                                                    : rankingMetric === 'signups'
                                                        ? 'Emails'
                                                        : rankingMetric === 'callBookings'
                                                            ? 'Calls'
                                                            : 'Sales'
                                            }</label>
                                            <span className="rank-with-tooltip body-medium-semibold">
                                                {latestVideoMetrics.rank} <span className="rank-arrow">›</span>
                                                <div className="rank-tooltip">
                                                    <h4>Top Recent Videos</h4>
                                                    <p className="tooltip-timeframe body-tiny-regular">{latestVideoMetrics.timeframe}</p>
                                                    {latestVideoMetrics.topVideos.map((video, index) => (                                                        <div key={index} className="tooltip-video-row">
                                                            <span className="tooltip-rank">#{index + 1}</span>
                                                            <img 
                                                                src={video.thumbnail} 
                                                                alt="" 
                                                                className="tooltip-thumbnail"
                                                            />
                                                            <span className="tooltip-title body-medium-medium">{video.title}</span>
                                                            
                                                            {rankingMetric === 'clicks' 
                                                                ? <span className="tooltip-clicks body-medium-medium">{video.clicks} clicks</span>
                                                                : rankingMetric === 'signups'
                                                                    ? <span className="tooltip-signups body-medium-medium">{video.signups} emails</span>
                                                                    : rankingMetric === 'callBookings'
                                                                        ? <span className="tooltip-callbookings body-medium-medium">{video.callBookings} bookings</span>
                                                                        : <span className="tooltip-conversions body-medium-medium">{video.sales} sales</span>}
                                                        </div>
                                                    ))}
                                                </div>
                                            </span>
                                        </div>
                                        <div className="metric metric-odd">
                                            <label className="body-small-regular">Clicks</label>
                                            <span className="body-medium-semibold">{latestVideoMetrics.clicks.toLocaleString()}</span>
                                        </div>
                                        {hasSignupOffers && (
                                            <div className="metric">
                                                <label className="body-small-regular">Email Signups</label>
                                                <span className="body-medium-semibold">{latestVideoMetrics.signups.toLocaleString()}</span>
                                            </div>
                                        )}
                                        <div className={`metric ${!hasSignupOffers ? '' : 'metric-odd'}`}>
                                            <label className="body-small-regular">Call Bookings</label>
                                            <span className="body-medium-semibold">{latestVideoMetrics.callBookings.toLocaleString()}</span>
                                        </div>
                                        <div className={`metric ${!hasSignupOffers ? 'metric-odd' : ''}`}>
                                            <label className="body-small-regular">Sales</label>
                                            <span className="body-medium-semibold">{latestVideoMetrics.sales.toLocaleString()}</span>
                                        </div>
                                        <div className={`metric ${!hasSignupOffers ? '' : 'metric-odd'}`}>
                                            <label className="body-small-regular">Click-Through Rate</label>
                                            <span className="body-medium-semibold">{latestVideoMetrics.ctr}%</span>
                                        </div>
                                    </div>
                                    <div className="powered-by-container">
                                        <div className="site-url-container">
                                            <img 
                                                src="/revit.jpeg" 
                                                alt="RevTrack" 
                                                className="site-url-image"
                                            />
                                            <p className="site-url">getrevtrack.com</p>
                                        </div>
                                    </div>
                                    </div>
                                </div>
                            </div>
                        ) : youtubeName && isLoading ? (
                            <div className="login-prompt-card">
                                <h3>Loading videos...</h3>
                                <p>Fetching your latest video performance data.</p>
                            </div>
                        ) : youtubeName ? (
                            <div className="login-prompt-card">
                                <h3>Unable to fetch latest videos</h3>
                                <p>When you post videos, they will show up here.</p>
                            </div>
                        ) : (
                            <div className="login-prompt-card">
                                <h3>Connect YouTube Account</h3>
                                <p>Please connect your YouTube account to see your latest video performance.</p>
                                <button 
                                    onClick={() => handleLoginWithYouTube(username)}
                                    className="youtube-login-button"
                                >
                                    <Icon name="youtube" size={20} />
                                    Login with YouTube
                                </button>
                            </div>
                        )}
                        
                        <div className="dashboard-main">
                            {funnel && (
                                <section className="funnel-card" aria-label="Funnel performance">
                                    <div className="funnel-card-heading">
                                        <h3 className="funnel-card-title body-medium-semibold">What changed in the last 30 days</h3>
                                    </div>
                                    {funnel.summaries.length > 0 && (
                                        <div className="funnel-summaries">               
                                            {funnel.summaries.map((item) => (
                                                <p key={item.key} className={`funnel-summary body-small-regular funnel-summary--${item.direction}`}>
                                                    <span className="funnel-summary-icon" aria-hidden="true">
                                                        {item.direction === 'up'
                                                            ? <TrendingUp size={16} strokeWidth={2.4} />
                                                            : <TrendingDown size={16} strokeWidth={2.4} />}
                                                    </span>
                                                    {item.text}
                                                </p>
                                            ))}
                                        </div>
                                    )}
                                    <div className="funnel-card-header">
                                        <label className="funnel-offer-select">
                                            <span className="funnel-offer-label">Offer</span>
                                            <select
                                                aria-label="Filter by offer"
                                                value={selectedOffer}
                                                onChange={(e) => setSelectedOffer(e.target.value)}
                                            >
                                                <option value="all">All offers</option>
                                                {offers.map((offer) => (
                                                    <option key={offer.id} value={offer.name}>
                                                        {offer.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                    </div>
                                    <div className="funnel-table-wrap">
                                        <table className="funnel-table">
                                            <thead>
                                                <tr>
                                                    <th>Stage</th>
                                                    <th>Last 30 days</th>
                                                    <th>Previous 30 days</th>
                                                    <th>
                                                        <span className="funnel-th-label">
                                                            Normal
                                                            <span className="funnel-th-help" tabIndex={0}>
                                                                <span className="funnel-th-caret" aria-hidden="true">›</span>
                                                                <span className="funnel-th-tooltip">Your average 30-day period over the past 12 months.</span>
                                                            </span>
                                                        </span>
                                                    </th>
                                                    <th>
                                                        <span className="funnel-th-label">
                                                            Change
                                                            <span className="funnel-th-help" tabIndex={0}>
                                                                <span className="funnel-th-caret" aria-hidden="true">›</span>
                                                                <span className="funnel-th-tooltip">Compared with the previous 30 days.</span>
                                                            </span>
                                                        </span>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {funnel.rows.map((row) => (
                                                    <tr key={row.key}>
                                                        <td className="funnel-stage">
                                                            <span className="funnel-stage-label">
                                                                {row.label}
                                                                {row.help && (
                                                                    <span
                                                                        className="metric-help"
                                                                        tabIndex={0}
                                                                        role="img"
                                                                        aria-label={row.help}
                                                                    >
                                                                        ?
                                                                        <span className="metric-help-tooltip">{row.help}</span>
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </td>
                                                        <td>{row.thisMonth}</td>
                                                        <td>{row.lastMonth}</td>
                                                        <td>{row.normal}</td>
                                                        <td className={`funnel-change ${row.direction}`}>{row.change}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </section>
                            )}

                            {topResources && (
                                <div className="top-performing-section">
                                    <div className="top-performing-header">
                                        <h2>What converted in the past 30 days. Make More of this.</h2>
                                        <div className="top-performing-tabs">
                                            <button
                                                className={topResourcesTab === 'sales' ? 'active' : ''}
                                                onClick={() => setTopResourcesTab('sales')}
                                            >
                                                Sales
                                            </button>
                                            {topResources.topCalls?.length > 0 && (
                                                <button
                                                    className={topResourcesTab === 'calls' ? 'active' : ''}
                                                    onClick={() => setTopResourcesTab('calls')}
                                                >
                                                    Calls
                                                </button>
                                            )}
                                            {topResources.topEmails?.length > 0 && (
                                                <button
                                                    className={topResourcesTab === 'emails' ? 'active' : ''}
                                                    onClick={() => setTopResourcesTab('emails')}
                                                >
                                                    Email signups
                                                </button>
                                            )}
                                            {topResources.topClicks?.length > 0 && (
                                                <button
                                                    className={topResourcesTab === 'clicks' ? 'active' : ''}
                                                    onClick={() => setTopResourcesTab('clicks')}
                                                >
                                                    Traffic
                                                </button>
                                            )}
                                        </div>
                                        <a href="/content" className="manage-offers-link">
                                            See all data →
                                        </a>
                                    </div>

                                    <div className="top-performing-table">
                                        <div className="table-header">
                                            <span className="col-rank">#</span>
                                            <span className="col-offer">CONTENT</span>
                                            <SortTh label="VIEWS" sortKey="views" sort={contentSort} onSort={handleContentSort} className="col-views" />
                                            <SortTh label="TRAFFIC" sortKey="clicks" sort={contentSort} onSort={handleContentSort} className="col-clicks" />
                                            {topResourcesTab !== 'clicks' && (
                                                <SortTh
                                                    label={topResourcesTab === 'sales' ? 'SALES' : topResourcesTab === 'calls' ? 'CALLS' : 'EMAILS'}
                                                    sortKey="metric"
                                                    sort={contentSort}
                                                    onSort={handleContentSort}
                                                    className="col-metric"
                                                />
                                            )}
                                            <SortTh label="CONV %" sortKey="conv" sort={contentSort} onSort={handleContentSort} className="col-conv" />
                                            {topResourcesTab === 'sales' && (
                                                <SortTh label="REVENUE" sortKey="revenue" sort={contentSort} onSort={handleContentSort} className="col-revenue" />
                                            )}
                                        </div>

                                        <div className="table-body">
                                            {(() => {
                                                const baseList =
                                                    topResourcesTab === 'sales' ? topResources.topRevenue :
                                                    topResourcesTab === 'calls' ? topResources.topCalls :
                                                    topResourcesTab === 'emails' ? topResources.topEmails :
                                                    topResources.topClicks;
                                                const cntGetVal = (key, r) => {
                                                    if (key === 'views') return r.views || 0;
                                                    if (key === 'clicks') return r.clicks || 0;
                                                    if (key === 'conv') return parseFloat(r.conversion_rate) || 0;
                                                    if (key === 'revenue') return r.revenue || 0;
                                                    if (key === 'metric') return topResourcesTab === 'sales' ? (r.sales || 0)
                                                        : topResourcesTab === 'calls' ? (r.call_bookings || 0)
                                                        : (r.email_signups || 0);
                                                    return 0;
                                                };
                                                const defaultSort = (a, b) => {
                                                    if (topResourcesTab === 'sales') return b.revenue - a.revenue;
                                                    if (topResourcesTab === 'calls') return b.call_bookings - a.call_bookings;
                                                    if (topResourcesTab === 'emails') return b.email_signups - a.email_signups;
                                                    return b.clicks - a.clicks;
                                                };
                                                const sorted = contentSort.key
                                                    ? [...(baseList || [])].sort((a, b) => {
                                                        const dir = contentSort.dir === 'desc' ? -1 : 1;
                                                        return (cntGetVal(contentSort.key, b) - cntGetVal(contentSort.key, a)) * dir;
                                                    })
                                                    : [...(baseList || [])].sort(defaultSort);
                                                return sorted.map((resource, index) => (
                                                    <div key={resource.resource_id} className="table-row">
                                                        <span className="col-rank">{String(index + 1).padStart(2, '0')}</span>
                                                        <div className="col-offer">
                                                            <div className="offer-title">{resource.youtube_title || resource.name}</div>
                                                        </div>
                                                        <span className="col-views">
                                                            {resource.views === 0 ? 'N/A' : formatNumber(resource.views)}
                                                        </span>
                                                        <span className="col-clicks">{formatNumber(resource.clicks)}</span>
                                                        {topResourcesTab !== 'clicks' && (
                                                            <span className="col-metric">
                                                                {topResourcesTab === 'sales' ? formatNumber(resource.sales) :
                                                                    topResourcesTab === 'calls' ? formatNumber(resource.call_bookings) :
                                                                    formatNumber(resource.email_signups)}
                                                            </span>
                                                        )}
                                                        <span className="col-conv">{`${resource.conversion_rate}%`}</span>
                                                        {topResourcesTab === 'sales' && (
                                                            <span className="col-revenue">{formatCurrency(resource.revenue)}</span>
                                                        )}
                                                    </div>
                                                ));
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    </div>
                )}
        </div>
    );
};

export default Content;
